/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


(function(){
  class FeatureOrbit extends HTMLElement {
    connectedCallback(){
      this._reposition = this._reposition.bind(this);
      this._observer = new ResizeObserver(this._reposition);
      const circle = this.querySelector('[data-role="orbit-circle"]');
      if (circle) this._observer.observe(circle);
      window.addEventListener('resize', this._reposition, { passive: true });
      document.addEventListener('shopify:section:load', this._reposition);
      requestAnimationFrame(this._reposition);
    }
    disconnectedCallback(){
      window.removeEventListener('resize', this._reposition);
      document.removeEventListener('shopify:section:load', this._reposition);
      if (this._observer) this._observer.disconnect();
    }
    _reposition(){
      const circle = this.querySelector('[data-role="orbit-circle"]');
      const features = this.querySelectorAll('[data-role="orbit-feature"]');
      if (!circle || !features.length) return;

      const rect = circle.getBoundingClientRect();
      const radius = rect.width / 2;
      const cx = rect.left + radius + window.scrollX;
      const cy = rect.top + radius + window.scrollY;

      // Get settings from data attributes
      const sectionId = this.getAttribute('data-section-id');
      const angle1 = parseInt(this.getAttribute('data-angle-1')) || 200;
      const angle2 = parseInt(this.getAttribute('data-angle-2')) || 25;
      const angle3 = parseInt(this.getAttribute('data-angle-3')) || 320;
      const labelOffset = parseInt(this.getAttribute('data-offset')) || 36;
      
      const isMobile = window.matchMedia('(max-width: 768px)').matches;

      // Position relative to the main container
      const containerRect = this.getBoundingClientRect();
      const containerLeft = containerRect.left + window.scrollX;
      const containerTop = containerRect.top + window.scrollY;

      // Use configured angles from settings
      const configuredAngles = [angle1, angle2, angle3];

      features.forEach((el, idx) => {
        const angleDeg = configuredAngles[(idx) % configuredAngles.length];
        const angleRad = (angleDeg * Math.PI) / 180;

        // Dot on the ring
        const dotX = cx + Math.cos(angleRad) * radius;
        const dotY = cy + Math.sin(angleRad) * radius;

        // Anchor point just outside the ring (radially outward)
        const anchorX = cx + Math.cos(angleRad) * (radius + labelOffset);
        const anchorY = cy + Math.sin(angleRad) * (radius + labelOffset);

        el.style.position = 'absolute';
        el.style.left = (anchorX - containerLeft) + 'px';
        el.style.top = (anchorY - containerTop) + 'px';
        el.style.transform = 'translate(-50%, -50%)';

        // Side is derived from angle: left side for ~200°, right side for ~25° and ~320°
        const isRight = angleDeg < 90 || angleDeg > 270;
        el.setAttribute('data-side', isRight ? 'right' : 'left');

        // Place the dot relative to the label anchor so it sits on the ring
        const dot = el.querySelector('[data-role="orbit-dot"]');
        if (dot) {
          dot.style.position = 'absolute';
          dot.style.left = '50%';
          dot.style.top = '50%';
          const relDX = dotX - anchorX;
          const relDY = dotY - anchorY;
          dot.style.transform = 'translate(' + relDX + 'px,' + relDY + 'px) translate(-50%, -50%)';
        }

        // Nudge the content further outward so details sit under the indicator
        const sectionId = this.getAttribute('data-section-id');
        const content = el.querySelector('.product-showcase__feature-content-' + sectionId);
        if (content) {
          const contentGap = 12; // px farther from the ring
          const contentDX = Math.cos(angleRad) * contentGap;
          const contentDY = Math.sin(angleRad) * contentGap;
          content.style.transform = 'translate(' + contentDX + 'px,' + contentDY + 'px)';
        }
      });
    }
  }
  if (!customElements.get('feature-orbit')) {
    customElements.define('feature-orbit', FeatureOrbit);
  }
})();


document.addEventListener("DOMContentLoaded", function () {
  const parent = document.querySelectorAll(".steps__container");
  if (parent) {
    parent.forEach((el) => {
      const buttons = el.querySelectorAll("button");
      buttons.forEach((btn) => {
        btn.addEventListener("click", function () {
          // change video
          let newSrc = btn.getAttribute("data-src");
          let vid = document.querySelector(
            `#${el.getAttribute("data-parent")} video`
          );
          let source = document.querySelector(
            `#${el.getAttribute("data-parent")} video source`
          );
          source.src = newSrc;
          vid.load();
          vid.play();

          setActive(buttons, btn, el.getAttribute("data-parent"));
        });
      });
    });

    function setActive(buttons, btn, parent) {
      buttons.forEach((el) => {
        el.classList.remove("active");
      });
      btn.classList.add("active");

      // content
      document
        .querySelectorAll(`#${parent} .image-with-text-content`)
        .forEach((el) => {
          document
            .querySelector(`#${parent} #${btn.getAttribute("data-content")}`)
            .classList.add("active");
          if (!(el.id == `${btn.getAttribute("data-content")}`)) {
            el.classList.remove("active");
          }
        });
    }
  }
});
